/**
 * time-group-by-utils.ts
 *
 * Grouping logic and subtotal calculations for the Time Entries tab.
 *
 * Supported group-by fields (categorical / low-cardinality only):
 *   - team-member      (TimeEntry.employeeName / employeeId)
 *   - work-type        (TimeEntry.workTypeId)
 *   - role             (TimeEntry.roleId)
 *   - cost-centre      (TimeEntry.costCentreId)
 *   - approval-status  (TimeEntry.approved → Approved / Pending)
 *   - week-commencing  (TimeEntry.date → Monday of that week)
 *
 * Subtotals per group:
 *   - count            number of entries
 *   - totalHours       sum of hours
 *   - billableHours    sum of hours where billable === true
 *   - pendingHours     sum of hours where approved === false
 *   - approvedHours    sum of hours where approved === true
 */

import type { TimeEntry } from '@/types';
import { startOfWeek, format } from 'date-fns';

// ─── Types ─────────────────────────────────────────────────────────────────────

export type TimeGroupById =
  | 'team-member'
  | 'work-type'
  | 'role'
  | 'cost-centre'
  | 'approval-status'
  | 'week-commencing';

export interface TimeGroupByOption {
  id: TimeGroupById;
  label: string;
}

export const TIME_GROUP_BY_OPTIONS: TimeGroupByOption[] = [
  { id: 'team-member',     label: 'Team Member' },
  { id: 'work-type',       label: 'Work Type' },
  { id: 'role',            label: 'Role' },
  { id: 'cost-centre',     label: 'Cost Centre' },
  { id: 'approval-status', label: 'Approval Status' },
  { id: 'week-commencing', label: 'Week Commencing' },
];

export interface TimeGroupSubtotals {
  count: number;
  totalHours: number;
  billableHours: number;
  pendingHours: number;
  approvedHours: number;
}

export interface TimeEntryGroup {
  /** Stable key used for collapse state */
  key: string;
  /** Human-readable label shown in the group header */
  label: string;
  /** The TimeGroupById field this group was created from */
  groupById: TimeGroupById;
  entries: TimeEntry[];
  subtotals: TimeGroupSubtotals;
}

// ─── Key extraction ────────────────────────────────────────────────────────────

function getGroupKey(entry: TimeEntry, groupBy: TimeGroupById): string {
  switch (groupBy) {
    case 'team-member':
      return entry.employeeName ?? String(entry.employeeId) ?? '__none__';

    case 'work-type':
      return entry.workTypeId ?? '__none__';

    case 'role':
      return entry.roleId ?? '__none__';

    case 'cost-centre':
      return entry.costCentreId ?? '__none__';

    case 'approval-status':
      return entry.approved ? 'approved' : 'pending';

    case 'week-commencing': {
      const monday = startOfWeek(new Date(entry.date), { weekStartsOn: 1 });
      return format(monday, 'yyyy-MM-dd');
    }

    default:
      return '__none__';
  }
}

function getGroupLabel(key: string, groupBy: TimeGroupById): string {
  if (key === '__none__') return 'Unassigned';

  switch (groupBy) {
    case 'approval-status':
      return key === 'approved' ? 'Approved' : 'Pending Approval';

    case 'week-commencing':
      return `W/C ${format(new Date(key), 'd MMM yyyy')}`;

    // For team-member, work-type, role, cost-centre — the key IS the display label
    default:
      return key;
  }
}

// ─── Subtotal calculation ──────────────────────────────────────────────────────

function calcSubtotals(entries: TimeEntry[]): TimeGroupSubtotals {
  return entries.reduce<TimeGroupSubtotals>(
    (acc, e) => {
      acc.count        += 1;
      acc.totalHours   += e.hours;
      acc.billableHours += e.billable ? e.hours : 0;
      acc.pendingHours  += !e.approved ? e.hours : 0;
      acc.approvedHours += e.approved ? e.hours : 0;
      return acc;
    },
    { count: 0, totalHours: 0, billableHours: 0, pendingHours: 0, approvedHours: 0 },
  );
}

// ─── Sort order for group headers ─────────────────────────────────────────────

function groupSortKey(group: TimeEntryGroup, groupBy: TimeGroupById): string {
  if (group.key === '__none__') return 'zzz';
  switch (groupBy) {
    case 'approval-status':
      // Pending first so outstanding items are top of mind
      return group.key === 'pending' ? '0' : '1';
    case 'week-commencing':
      // Chronological — key is already ISO yyyy-MM-dd
      return group.key;
    default:
      return group.label.toLowerCase();
  }
}

// ─── Main grouping function ────────────────────────────────────────────────────

/**
 * Buckets an array of TimeEntry objects into groups for the first Group By field.
 * (Single-level grouping — the Time tab uses one Group By field at a time.)
 */
export function groupTimeEntries(
  entries: TimeEntry[],
  groupBy: TimeGroupById,
): TimeEntryGroup[] {
  const map = new Map<string, TimeEntry[]>();

  for (const entry of entries) {
    const key = getGroupKey(entry, groupBy);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(entry);
  }

  const groups: TimeEntryGroup[] = [];

  for (const [rawKey, groupEntries] of map.entries()) {
    groups.push({
      key: rawKey,
      label: getGroupLabel(rawKey, groupBy),
      groupById: groupBy,
      entries: groupEntries,
      subtotals: calcSubtotals(groupEntries),
    });
  }

  groups.sort((a, b) =>
    groupSortKey(a, groupBy).localeCompare(groupSortKey(b, groupBy)),
  );

  return groups;
}
